import React, { useState, useMemo } from "react";
import reviewsService from "../services/reviewsService";
import OrderModal from "./OrderModal";
import { IoArrowForward } from "react-icons/io5";
import { FaFacebookF, FaInstagram, FaStar, FaWaze } from "react-icons/fa";
import LOGO_IMG from "../assets/images/logo.png";
import DECO_TR from "../assets/images/toptreeright.png";
import DECO_BL from "../assets/images/leftimagebottom.png";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
// ── Image optimisation ───────────────────────────────────────────────────────
// Converts a Supabase object URL to the render/transform endpoint so the
// storage service resizes + compresses on the fly, saving bandwidth.
// Non-Supabase URLs are returned unchanged.
function sbImg(url, width = 600, quality = 75) {
    if (!url) return url;
    try {
        const u = new URL(url);
        if (!u.hostname.endsWith(".supabase.co")) return url;
        // /storage/v1/object/public/... → /storage/v1/render/image/public/...
        const transformed = url.replace(
            "/storage/v1/object/public/",
            "/storage/v1/render/image/public/"
        );
        const sep = transformed.includes("?") ? "&" : "?";
        return `${transformed}${sep}width=${width}&quality=${quality}&resize=contain`;
    } catch {
        return url;
    }
}

// ── Design tokens (Figma DOME-TERA) ─────────────────────────────────────────
const BROWN = "#55260c";
const BG = "#fbf3ef";
const FONT_SCRIPT = "'WindSong', cursive";
const FONT_TITLE = "'Ysabeau Infant', sans-serif";
const FONT_HEB = "'IBM Plex Sans Hebrew', 'Heebo', sans-serif";

// ── Figma background decorative assets (Figma DOME-TERA node 9:11) ──────────

// ── Figma featured section assets (node 124:383) ─────────────────────────────

// ── Helpers ──────────────────────────────────────────────────────────────────
function catName(cat) {
    if (!cat) return "";
    const n = cat.categoryName;
    return typeof n === "object" ? (n.he ?? n.en ?? "") : (n ?? "");
}
function subCatName(sub) {
    if (!sub) return "";
    const n = sub.subCategoryName;
    return typeof n === "object" ? (n.he ?? n.en ?? "") : (n ?? "");
}
function dishTitle(dish) {
    if (!dish) return "";
    const n = dish.dishName;
    return typeof n === "object" ? (n.he ?? n.en ?? "") : (n ?? "");
}
function dishDesc(dish) {
    if (!dish) return "";
    const d = dish.description;
    return typeof d === "object" ? (d.he ?? d.en ?? "") : (d ?? "");
}
function restaurantDesc(r) {
    if (!r) return "";
    const d = r.description;
    return typeof d === "object" ? (d.he ?? d.en ?? "") : (d ?? "");
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ restaurant }) {
    return (
        <div
            className="text-center py-6"
            style={{ backgroundColor: "#5F6C37", color: "white", position: "relative", zIndex: 1, marginTop: "auto", alignSelf: "stretch", width: "100%" }}
        >
            <div className="flex justify-center gap-6 text-lg mb-2">
                {restaurant?.facebookLink && (
                    <a href={restaurant.facebookLink} target="_blank" rel="noreferrer" aria-label="Facebook" style={{ color: "white" }}>
                        <FaFacebookF />
                    </a>
                )}
                {restaurant?.instagramLink && (
                    <a href={restaurant.instagramLink} target="_blank" rel="noreferrer" aria-label="Instagram" style={{ color: "white" }}>
                        <FaInstagram />
                    </a>
                )}
                {restaurant?.wazeLink && (
                    <a href={restaurant.wazeLink} target="_blank" rel="noreferrer" aria-label="Waze" style={{ color: "white" }}>
                        <FaWaze />
                    </a>
                )}
            </div>
            <p className="text-xs opacity-70"> כל הזכויות שמורות לדומטרה©</p>
            <p className="text-xs mt-1 opacity-70">powered by <a href="https://coreweb.co.il" target="_blank" rel="noreferrer noopener" style={{ color: "inherit" }}>coreweb</a></p>
        </div>
    );
}

// ── TitleBlock ────────────────────────────────────────────────────────────────
function TitleBlock() {
    return (
        <div className="text-center mt-10" style={{ paddingTop: 28, paddingBottom: 12 }}>
            <div
                style={{
                    fontFamily: FONT_SCRIPT,
                    fontSize: 44,
                    color: BROWN,
                    lineHeight: 0.3,
                }}
            >
                the
            </div>
            <div
                style={{
                    fontFamily: FONT_TITLE,
                    fontWeight: 500,
                    fontSize: 52,
                    color: BROWN,
                    lineHeight: 0.9,
                    letterSpacing: 2,
                }}
            >
                MENU
            </div>
        </div>
    );
}

// ── CategoryRow ───────────────────────────────────────────────────────────────
// Figma 124:382 exact layout (440px page, card 237×113):
//   Right-side cards (left=179, right margin=24):
//     image left=8px inside card, top=-8px (vertical overflow), width=117, height=118
//     text centered in right portion (left:130 → right:14)
//   Left-side cards (left=32, left margin=32):
//     image right=8px inside card, top=-8px, width=117, height=118
//     text centered in left portion (right:130 → left:14)
//   Background: rgba(255,248,248,0.8) — plain rectangle, no border-radius, no effects.
function CategoryRow({ cat, index, onClick }) {
    const isRight = index % 2 === 0;
    const name = catName(cat);
    const img = cat.categoryImage;

    const outerStyle = isRight
        ? { marginLeft: "auto", marginRight: 24 }
        : { marginRight: "auto", marginLeft: 32 };

    return (
        <button
            type="button"
            onClick={onClick}
            className="focus:outline-none active:scale-[0.97] transition-transform"
            style={{
                ...outerStyle,
                position: "relative",
                width: 320,
                height: 113,
                background: "none",
                border: "none",
                outline: "none",
                overflow: "visible",
                display: "block",
                cursor: "pointer",
                WebkitAppearance: "none",
                appearance: "none",
            }}
        >
            {/* Painted-blob background — exact Figma SVG (node 124:436).
          feTurbulence + feDisplacementMap warps the rect into the organic shape.
          SVG is 273×149 (237+36 × 113+36) to allow displacement overflow;
          positioned at -18px so it stays centred over the 237×113 card. */}
            <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="356"
                height="149"
                viewBox="0 0 356 149"
                fill="none"
                style={{
                    position: "absolute",
                    top: -18,
                    left: -18,
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            >
                <g filter={`url(#bg-filter-${index})`}>
                    <rect x="18" y="18" width="320" height="113" fill="#FFF8F8" fillOpacity="0.8" />
                </g>
                <defs>
                    <filter
                        id={`bg-filter-${index}`}
                        x="0"
                        y="0"
                        width="356"
                        height="149"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feTurbulence
                            type="fractalNoise"
                            baseFrequency="0.0555555559694767 0.0555555559694767"
                            numOctaves="3"
                            seed="3198"
                        />
                        <feDisplacementMap
                            in="shape"
                            scale="36"
                            xChannelSelector="R"
                            yChannelSelector="G"
                            result="displacedImage"
                            width="100%"
                            height="100%"
                        />
                        <feMerge result="effect1_texture_124_436">
                            <feMergeNode in="displacedImage" />
                        </feMerge>
                    </filter>
                </defs>
            </svg>

            {/* Image + Name — flex row, image on the near edge, name immediately beside it */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: isRight ? "row" : "row-reverse",
                    alignItems: "center",
                    zIndex: 1,
                    pointerEvents: "none",
                }}
            >
                {/* Image – 8px from near edge, overflows 8px above */}
                <div
                    style={{
                        width: 117,
                        height: 118,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: -8,
                        ...(isRight ? { marginLeft: 8 } : { marginRight: 8 }),
                    }}
                >
                    {img ? (
                        <img
                            src={sbImg(img, 240)}
                            alt={name}
                            loading="lazy"
                            decoding="async"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        <div style={{ width: "100%", height: "100%", backgroundColor: "rgba(85,38,12,0.06)" }} />
                    )}
                </div>

                {/* 12px gap between image and name */}
                <div style={{ width: 12, flexShrink: 0 }} />

                {/* Name – fills remaining width, 14px padding on the far edge */}
                <span
                    style={{
                        flex: 1,
                        fontFamily: FONT_HEB,
                        fontSize: 16.631,
                        fontWeight: 500,
                        color: BROWN,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: isRight ? "left" : "right",
                        ...(isRight ? { paddingRight: 14 } : { paddingLeft: 14 }),
                        pointerEvents: "auto",
                    }}
                >
                    {name}
                </span>
            </div>
        </button>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
function Home({ restaurantData, loading, showIntro, onRestaurantDataRefresh }) {
    const restaurant = restaurantData?.restaurant;
    const dishes = useMemo(() => restaurantData?.dishes || [], [restaurantData]);
    const dishCategories = useMemo(() => restaurantData?.dishCategories || [], [restaurantData]);
    const subCategories = useMemo(() => restaurantData?.subCategories || [], [restaurantData]);
    const galleryImages = useMemo(
        () => (restaurantData?.galleryImages || []).filter((img) => img.isVisible !== false),
        [restaurantData]
    );
    const approvedReviews = useMemo(
        () => (Array.isArray(restaurantData?.reviews) ? restaurantData.reviews : []),
        [restaurantData?.reviews]
    );
    const [view, setView] = useState("landing");
    const [selectedCatId, setSelectedCatId] = useState(null);
    const [selectedSubId, setSelectedSubId] = useState(null);
    const [fade, setFade] = useState(true);
    const [landingExit, setLandingExit] = useState(false);

    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewName, setReviewName] = useState("");
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewContent, setReviewContent] = useState("");
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewFeedback, setReviewFeedback] = useState(null);

    // ── Ordering state ────────────────────────────────────────────────────────
    const orderingEnabled = !!restaurant?.orderingEnabled;
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const dishAddOns = useMemo(() => restaurantData?.dishAddOns || [], [restaurantData]);
    const dishAddOnCategories = useMemo(() => restaurantData?.dishAddOnCategories || [], [restaurantData]);

    // Categories sorted by displayIndex
    const sortedCategories = useMemo(
        () => [...dishCategories].sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0)),
        [dishCategories]
    );

    // Subcategories for the selected category
    const subCatsForSelected = useMemo(
        () =>
            selectedCatId
                ? subCategories
                    .filter((s) => s.parentCategoryId === selectedCatId)
                    .sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0))
                : [],
        [subCategories, selectedCatId]
    );
    // Dishes for the current selection
    const selectedDishes = useMemo(() => {
        if (!selectedCatId) return [];
        const list = selectedSubId
            ? dishes.filter((d) => d.subCategoryId === selectedSubId)
            : dishes.filter(
                (d) => d.categoryId === selectedCatId && (!d.subCategoryId || d.subCategoryId === "")
            );
        return list
            .filter((d) => d.isAvailable !== false)
            .sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0));
    }, [dishes, selectedCatId, selectedSubId]);

    const selectedCat = dishCategories.find((c) => c._id === selectedCatId);
    const selectedSub = subCategories.find((s) => s._id === selectedSubId);

    function navigate(nextView, catId, subId) {
        setFade(false);
        setTimeout(() => {
            setSelectedCatId(catId);
            setSelectedSubId(subId);
            if (nextView === "landing") setLandingExit(false);
            setView(nextView);
            setFade(true);
        }, 180);
    }

    function handleCategoryClick(cat) {
        const hasSubs = subCategories.some((s) => s.parentCategoryId === cat._id);
        navigate(hasSubs ? "subcategories" : "dishes", cat._id, null);
    }

    function handleSubClick(sub) {
        navigate("dishes", selectedCatId, sub._id);
    }

    function goBack() {
        if (view === "dishes" && selectedSubId) {
            navigate("subcategories", selectedCatId, null);
        } else {
            navigate("categories", null, null);
        }
    }

    async function handleSubmitReview() {
        if (!restaurant?._id) return;
        const name = reviewName.trim();
        const content = reviewContent.trim();
        if (!name || !content) {
            setReviewFeedback("fill");
            return;
        }
        setReviewSubmitting(true);
        setReviewFeedback(null);
        try {
            await reviewsService.createReview({
                restaurantId: restaurant._id,
                userName: name,
                content,
                rating: reviewRating,
            });
            if (onRestaurantDataRefresh) {
                await onRestaurantDataRefresh();
            }
            setReviewFeedback("thanks");
            setReviewName("");
            setReviewContent("");
            setReviewRating(5);
            setTimeout(() => {
                setReviewModalOpen(false);
                setReviewFeedback(null);
            }, 2200);
        } catch {
            setReviewFeedback("error");
        } finally {
            setReviewSubmitting(false);
        }
    }

    // ── SPLASH LOADER ─────────────────────────────────────────────────────────
    if (showIntro || loading || !restaurantData) {
        return (
            <>
                <style>{`
                    @keyframes dt-spin {
                        from { transform: rotate(0deg);   }
                        to   { transform: rotate(360deg); }
                    }
                    @keyframes dt-pulse-glow {
                        0%, 100% { transform: scale(1);    opacity: 0.08; }
                        50%      { transform: scale(1.12); opacity: 0.16; }
                    }
                    @keyframes dt-reveal {
                        from { opacity: 0; transform: translateY(18px); }
                        to   { opacity: 1; transform: translateY(0);    }
                    }
                    @keyframes dt-shimmer {
                        0%   { transform: translateX(-100%); }
                        100% { transform: translateX(500%);  }
                    }
                `}</style>
                <div style={{
                    width: "100%", height: "100vh",
                    backgroundColor: BG,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    position: "relative", overflow: "hidden",
                }}>
                    {/* Pulsing radial glow behind ornament */}
                    <div style={{
                        position: "absolute",
                        width: 320, height: 320, borderRadius: "50%",
                        background: `radial-gradient(circle, rgba(85,38,12,0.09) 0%, transparent 70%)`,
                        animation: "dt-pulse-glow 3.5s ease-in-out infinite",
                        pointerEvents: "none",
                    }} />

                    {/* Slowly spinning botanical ornament */}
                    <img
                        src={LOGO_IMG}
                        alt=""
                        aria-hidden="true"
                        style={{
                            width: 120, height: 120,
                            objectFit: "contain",
                            opacity: 0.2,
                            animation: "dt-spin 14s linear infinite",
                            marginBottom: 28,
                        }}
                    />

                    {/* Brand name — reveals upward */}
                    <div style={{ textAlign: "center", animation: "dt-reveal 0.9s cubic-bezier(0.22,1,0.36,1) 0.15s both" }}>
                        <div style={{ fontFamily: FONT_SCRIPT, fontSize: 36, color: BROWN, lineHeight: 0.55, opacity: 0.85 }}>the</div>
                        <div style={{ fontFamily: FONT_TITLE, fontWeight: 500, fontSize: 46, color: BROWN, letterSpacing: 6, lineHeight: 1.15 }}>DOME TERRA</div>
                    </div>

                    {/* Elegant shimmer bar */}
                    <div style={{
                        marginTop: 28,
                        width: 100, height: 2,
                        borderRadius: 2,
                        backgroundColor: `rgba(85,38,12,0.1)`,
                        overflow: "hidden",
                        animation: "dt-reveal 0.9s cubic-bezier(0.22,1,0.36,1) 0.35s both",
                    }}>
                        <div style={{
                            width: "35%", height: "100%",
                            background: `linear-gradient(90deg, transparent, rgba(85,38,12,0.55), transparent)`,
                            animation: "dt-shimmer 1.6s ease-in-out infinite",
                        }} />
                    </div>

                    {/* Corner botanical accents */}
                    <img src={DECO_TR} alt="" aria-hidden="true" style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, objectFit: "contain", opacity: 0.08, transform: "rotate(30deg)", pointerEvents: "none" }} />
                    <img src={DECO_BL} alt="" aria-hidden="true" style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, objectFit: "contain", opacity: 0.08, transform: "rotate(210deg)", pointerEvents: "none" }} />
                </div>
            </>
        );
    }

    // ── LANDING SCREEN (logo, description, Menu + Instagram buttons) ──────────
    if (view === "landing") {
        const logo = restaurant?.logo;
        const restaurantImage = restaurant?.restaurantImage;
        const description = restaurantDesc(restaurant);
        const instagramUrl = restaurant?.instagramLink;
        const isLoading = false;

        return (
            <div
                className="w-full flex flex-col"
                style={{
                    backgroundColor: BG,
                    position: "relative",
                    overflowX: "hidden",
                    minHeight: "100vh",
                    opacity: landingExit ? 0 : 1,
                    transform: landingExit ? "translateY(-20px) scale(0.97)" : "translateY(0) scale(1)",
                    transition: "opacity 0.35s ease, transform 0.35s ease",
                }}
            >
                {/* Hero image — top portion of screen */}
                <div style={{ position: "relative", width: "100%", flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {restaurantImage ? (
                        <img
                            src={sbImg(restaurantImage, 900)}
                            alt=""
                            decoding="async"
                            style={{ width: "80%", height: "80%", objectFit: "cover", display: "flex", justifyContent: "center", alignItems: "center" }}
                        />
                    ) : isLoading ? (
                        <div className="animate-pulse" style={{ width: "80%", height: "80%", backgroundColor: "rgba(85,38,12,0.08)" }} />
                    ) : (
                        <div style={{ width: "80%", height: "80%", backgroundColor: "rgba(85,38,12,0.05)" }} />
                    )}
                    {/* Gradient fade into BG color */}
                  
                </div>

                {/* Content area — logo, description, buttons */}
                <div
                    className="flex flex-col items-center px-6"
                    style={{ flex: 1, marginTop: -48, position: "relative", zIndex: 1, paddingBottom: 0, justifyContent: "flex-start", paddingTop: 0 }}
                >
                    {/* Logo */}
                    {logo ? (
                        <img
                            src={sbImg(logo, 200)}
                            alt=""
                            decoding="async"
                            className={isLoading ? "animate-pulse" : ""}
                            style={{ width: 70, height: 44, objectFit: "contain", marginBottom: 16 }}
                        />
                    ) : isLoading ? (
                        <div className="rounded-xl animate-pulse" style={{ width: 80, height: 48, marginBottom: 16, backgroundColor: "rgba(85,38,12,0.08)" }} />
                    ) : null}

                    {/* Restaurant description */}
                    {description ? (
                        <p
                            dir="auto"
                            className={`text-center ${isLoading ? "animate-pulse" : ""}`}
                            style={{
                                fontFamily: FONT_HEB,
                                fontSize: 15,
                                color: BROWN,
                                lineHeight: 1.7,
                                maxWidth: 300,
                                opacity: isLoading ? 0.5 : 0.8,
                            }}
                        >
                            {description}
                        </p>
                    ) : isLoading ? (
                        <div className="flex flex-col gap-2 items-center">
                            <div className="rounded animate-pulse" style={{ width: 220, height: 13, backgroundColor: "rgba(85,38,12,0.08)" }} />
                            <div className="rounded animate-pulse" style={{ width: 170, height: 13, backgroundColor: "rgba(85,38,12,0.08)" }} />
                        </div>
                    ) : null}

                    <div style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <p
                            dir="rtl"
                            style={{
                                fontFamily: "'Amiri', serif",
                                fontSize: 18,
                                fontWeight: 400,
                                color: BROWN,
                                opacity: 0.75,
                                margin: 0,
                                textAlign: "center",
                                fontStyle: "italic",
                                lineHeight: 1.6,
                            }}
                        >
                            حيثُ تَلتَقي السماء مَع الأَرض
                        </p>
                        <p
                            dir="rtl"
                            style={{
                                fontFamily: FONT_HEB,
                                fontSize: 15,
                                fontWeight: 400,
                                color: BROWN,
                                opacity: 0.65,
                                margin: 0,
                                textAlign: "center",
                                lineHeight: 1.6,
                            }}
                        >
                            כשהכיפה נפגשת עם הטבע המקום פועם בקסם וביופי
                        </p>
                    </div>

                    {/* Two buttons: Menu & Instagram */}
                    <div className="flex flex-col gap-3 w-full" style={{ maxWidth: 280, marginTop: 32 }}>
                        <button
                            type="button"
                            onClick={() => {
                                if (isLoading) return;
                                setLandingExit(true);
                                setTimeout(() => {
                                    setFade(false);
                                    setView("categories");
                                    setTimeout(() => setFade(true), 40);
                                }, 340);
                            }}
                            disabled={isLoading}
                            className="active:scale-[0.97] transition-transform focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                fontFamily: FONT_HEB,
                                fontWeight: 600,
                                fontSize: 17,
                                color: "white",
                                backgroundColor: "#5F6C37",
                                padding: "15px 24px",
                                borderRadius: 14,
                                border: "none",
                                cursor: isLoading ? "not-allowed" : "pointer",
                                letterSpacing: 0.3,
                            }}
                        >
                            לתפריט / Menu
                        </button>
                        <a
                            href={instagramUrl || "#"}
                            target={instagramUrl ? "_blank" : undefined}
                            rel={instagramUrl ? "noreferrer noopener" : undefined}
                            className={`flex items-center justify-center gap-2 active:scale-[0.97] transition-transform no-underline ${isLoading || !instagramUrl ? "pointer-events-none opacity-50" : ""}`}
                            style={{
                                fontFamily: FONT_HEB,
                                fontWeight: 500,
                                fontSize: 17,
                                color: BROWN,
                                backgroundColor: "transparent",
                                padding: "13px 24px",
                                borderRadius: 14,
                                border: `2px solid rgba(85,38,12,0.30)`,
                                textDecoration: "none",
                            }}
                        >
                            <FaInstagram size={20} />
                            Instagram
                        </a>
                        <button
                            type="button"
                            dir="rtl"
                            onClick={() => {
                                setReviewFeedback(null);
                                setReviewModalOpen(true);
                            }}
                            className="flex w-full items-center justify-center gap-3 active:scale-[0.97] transition-transform focus:outline-none"
                            style={{
                                fontWeight: 500,
                                fontSize: 13,
                                color: BROWN,
                                backgroundColor: "transparent",
                                padding: "13px 24px",
                                borderRadius: 14,
                                border: `2px solid rgba(85,38,12,0.30)`,
                                cursor: "pointer",
                                textDecoration: "none",
                                fontFamily: FONT_HEB,
                                letterSpacing: 0.02,
                            }}
                        >
                            <span className="flex items-center gap-2">
                                <FaStar size={20} aria-hidden className="shrink-0" />
                                השאר ביקורת / Write a Review
                            </span>
                        </button>
                    </div>

                    {reviewModalOpen && (
                        <div
                            role="dialog"
                            aria-modal="true"
                            style={{
                                position: "fixed",
                                inset: 0,
                                zIndex: 100,
                                backgroundColor: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 16,
                            }}
                            onClick={() => !reviewSubmitting && setReviewModalOpen(false)}
                        >
                            <div
                                dir="rtl"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    backgroundColor: "#fff",
                                    borderRadius: 16,
                                    padding: 20,
                                    maxWidth: 360,
                                    width: "100%",
                                    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                                    textAlign: "right",
                                }}
                            >
                                <h2
                                    style={{
                                        fontFamily: FONT_HEB,
                                        fontSize: 18,
                                        color: BROWN,
                                        margin: "0 0 12px",
                                        textAlign: "right",
                                    }}
                                >
                                    ביקורת
                                </h2>
                                <label
                                    style={{
                                        display: "block",
                                        fontFamily: FONT_HEB,
                                        fontSize: 13,
                                        color: BROWN,
                                        marginBottom: 4,
                                        textAlign: "right",
                                    }}
                                >
                                    שם
                                </label>
                                <input
                                    dir="rtl"
                                    value={reviewName}
                                    onChange={(e) => setReviewName(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: `1px solid rgba(85,38,12,0.2)`,
                                        fontFamily: FONT_HEB,
                                        marginBottom: 12,
                                        textAlign: "right",
                                    }}
                                />
                                <div style={{ marginBottom: 12 }}>
                                    <span
                                        style={{
                                            fontFamily: FONT_HEB,
                                            fontSize: 13,
                                            color: BROWN,
                                            display: "block",
                                            marginBottom: 6,
                                            textAlign: "right",
                                        }}
                                    >
                                        דירוג
                                    </span>
                                    <div
                                        dir="rtl"
                                        style={{ display: "flex", gap: 6, flexDirection: "row", justifyContent: "flex-start" }}
                                    >
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onClick={() => setReviewRating(n)}
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 8,
                                                    border: n <= reviewRating ? "2px solid #5F6C37" : "1px solid rgba(85,38,12,0.2)",
                                                    background: n <= reviewRating ? "rgba(95,108,55,0.15)" : "#fff",
                                                    cursor: "pointer",
                                                    fontSize: 18,
                                                }}
                                            >
                                                ★
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <label
                                    style={{
                                        display: "block",
                                        fontFamily: FONT_HEB,
                                        fontSize: 13,
                                        color: BROWN,
                                        marginBottom: 4,
                                        textAlign: "right",
                                    }}
                                >
                                    תוכן
                                </label>
                                <textarea
                                    dir="rtl"
                                    value={reviewContent}
                                    onChange={(e) => setReviewContent(e.target.value)}
                                    rows={4}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: `1px solid rgba(85,38,12,0.2)`,
                                        fontFamily: FONT_HEB,
                                        resize: "vertical",
                                        marginBottom: 12,
                                        textAlign: "right",
                                    }}
                                />
                                {reviewFeedback === "fill" && (
                                    <p
                                        style={{
                                            color: "#b91c1c",
                                            fontFamily: FONT_HEB,
                                            fontSize: 13,
                                            margin: "0 0 8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        נא למלא שם ותוכן
                                    </p>
                                )}
                                {reviewFeedback === "thanks" && (
                                    <p
                                        style={{
                                            color: "#15803d",
                                            fontFamily: FONT_HEB,
                                            fontSize: 13,
                                            margin: "0 0 8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        תודה! הביקורת תפורסם לאחר אישור המסעדה.
                                    </p>
                                )}
                                {reviewFeedback === "error" && (
                                    <p
                                        style={{
                                            color: "#b91c1c",
                                            fontFamily: FONT_HEB,
                                            fontSize: 13,
                                            margin: "0 0 8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        שגיאה בשליחה. נסו שוב.
                                    </p>
                                )}
                                <div dir="rtl" style={{ display: "flex", gap: 8, justifyContent: "flex-start", flexWrap: "wrap" }}>
                                    <button
                                        type="button"
                                        onClick={handleSubmitReview}
                                        disabled={reviewSubmitting}
                                        style={{
                                            padding: "10px 16px",
                                            borderRadius: 10,
                                            border: "none",
                                            background: "#5F6C37",
                                            color: "#fff",
                                            fontFamily: FONT_HEB,
                                            fontWeight: 600,
                                            cursor: reviewSubmitting ? "wait" : "pointer",
                                        }}
                                    >
                                        {reviewSubmitting ? "שולח…" : "שליחה"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setReviewModalOpen(false)}
                                        disabled={reviewSubmitting}
                                        style={{
                                            padding: "10px 16px",
                                            borderRadius: 10,
                                            border: `1px solid rgba(85,38,12,0.25)`,
                                            background: "#fff",
                                            fontFamily: FONT_HEB,
                                            cursor: reviewSubmitting ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {approvedReviews.length > 0 && (
                        <div style={{ width: "100%", maxWidth: 320, marginTop: 28 }}>
                            <h3
                                dir="rtl"
                                style={{
                                    fontFamily: FONT_HEB,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: BROWN,
                                    textAlign: "center",
                                    marginBottom: 12,
                                }}
                            >
                                ביקורות
                            </h3>
                            <Swiper
                                modules={[Pagination]}
                                dir="rtl"
                                slidesPerView={1}
                                spaceBetween={14}
                                grabCursor
                                pagination={{ clickable: true, dynamicBullets: approvedReviews.length > 5 }}
                                className="reviews-swiper"
                                style={{
                                    paddingBottom: 36,
                                    width: "100%",
                                    "--swiper-theme-color": BROWN,
                                    "--swiper-pagination-bullet-inactive-color": "rgba(85,38,12,0.35)",
                                }}
                            >
                                {approvedReviews.map((rev) => (
                                    <SwiperSlide key={rev._id}>
                                        <div
                                            dir="rtl"
                                            style={{
                                                backgroundColor: "rgba(255,248,248,0.9)",
                                                borderRadius: 12,
                                                padding: "12px 14px",
                                                textAlign: "right",
                                                boxSizing: "border-box",
                                                minHeight: 100,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: 6,
                                                    flexDirection: "row",
                                                }}
                                            >
                                                <span style={{ fontFamily: FONT_HEB, fontWeight: 600, color: BROWN }}>{rev.userName}</span>
                                                <span style={{ color: "#5F6C37", fontSize: 14, unicodeBidi: "plaintext" }}>
                                                    {"★".repeat(rev.rating)}
                                                </span>
                                            </div>
                                            <p
                                                dir="rtl"
                                                style={{
                                                    fontFamily: FONT_HEB,
                                                    fontSize: 14,
                                                    color: BROWN,
                                                    margin: 0,
                                                    lineHeight: 1.6,
                                                    opacity: 0.9,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {rev.content}
                                            </p>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    )}

                    {/* 3D Gallery */}
                    {!isLoading && galleryImages.length > 0 && (
                        <div style={{ width: "100%", marginTop: 32 }}>
                            <Swiper
                                modules={[EffectCoverflow, Autoplay]}
                                effect="coverflow"
                                grabCursor
                                centeredSlides
                                slidesPerView="auto"
                                loop
                                autoplay={{ delay: 2200, disableOnInteraction: false }}
                                coverflowEffect={{
                                    rotate: 38,
                                    stretch: 0,
                                    depth: 120,
                                    modifier: 1.5,
                                    slideShadows: true,
                                }}
                                style={{ paddingTop: 10, paddingBottom: 10 }}
                            >
                                {galleryImages.map((img) => (
                                    <SwiperSlide key={img._id} style={{ width: 160, height: 190, borderRadius: 16, overflow: "hidden", flexShrink: 0 }}>
                                        <img
                                            src={sbImg(img.imageUrl, 320)}
                                            alt={img.caption || ""}
                                            loading="lazy"
                                            decoding="async"
                                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    )}
                </div>

                {/* Subtle corner decoration */}
                <Footer restaurant={restaurant} />
            </div>
        );
    }

    // ── PAGE WRAPPER ──────────────────────────────────────────────────────────
    return (
        <>
        {/* Order modal */}
        {orderModalOpen && (
            <OrderModal
                restaurant={restaurant}
                dishes={dishes}
                dishCategories={dishCategories}
                subCategories={subCategories}
                dishAddOns={dishAddOns}
                dishAddOnCategories={dishAddOnCategories}
                onClose={() => setOrderModalOpen(false)}
            />
        )}
        <div
            className="w-full min-h-screen flex flex-col"
            style={{
                backgroundColor: BG,
                maxWidth: 440,
                margin: "0 auto",
                position: "relative",
                opacity: fade ? 1 : 0,
                transform: fade ? "translateY(0)" : "translateY(28px)",
                transition: "opacity 0.32s ease, transform 0.32s ease",
            }}
        >
            {/* ── Shared background decorations — clipped inside own div so main container has no overflow setting ── */}
            <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
                <img src={DECO_TR} alt="" style={{ position: "absolute", right: -40, top: 30, width: 200, height: 200, objectFit: "contain", opacity: 0.08 }} />
                <img src={DECO_BL} alt="" style={{ position: "absolute", left: -40, bottom: 60, width: 200, height: 200, objectFit: "contain", opacity: 0.08 }} />
            </div>

            {/* ══════════════════ CATEGORIES VIEW ══════════════════════════════════ */}
            {view === "categories" && (
                <div className="flex flex-col flex-1" style={{ position: "relative", minHeight: "100vh" }}>
                    <div style={{ position: "relative", zIndex: 1 }}>
                        {/* Back to landing */}
                        <button
                            type="button"
                            onClick={() => navigate("landing", null, null)}
                            aria-label="חזור"
                            className="focus:outline-none"
                            style={{ position: "absolute", right: 20, top: 18, background: "none", border: "none", cursor: "pointer", padding: 8, zIndex: 2 }}
                        >
                            <IoArrowForward size={27} style={{ color: BROWN }} />
                        </button>
                        <TitleBlock />
                    </div>

                    {/* Staggered category rows – ltr so left/right CSS matches Figma coords */}
                    <div
                        dir="ltr"
                        style={{ display: "flex", flexDirection: "column", gap: 18, padding: "16px 0 32px", position: "relative", zIndex: 1 }}
                    >
                        {sortedCategories.map((cat, i) => (
                            <CategoryRow
                                key={cat._id}
                                cat={cat}
                                index={i}
                                onClick={() => handleCategoryClick(cat)}
                            />
                        ))}
                    </div>

                    {orderingEnabled && (
                        <button type="button" onClick={() => setOrderModalOpen(true)}
                            style={{ background: "#5F6C37", color: "white", border: "none", borderRadius: 50, padding: "14px 40px", fontFamily: FONT_HEB, fontSize: 16, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 18px rgba(0,0,0,0.18)", whiteSpace: "nowrap", direction: "rtl", margin: "8px auto 4px" }}>
                             להזמנה
                        </button>
                    )}
                    <Footer restaurant={restaurant} />
                </div>
            )}

            {/* ══════════════════ SUBCATEGORIES VIEW ═══════════════════════════════ */}
            {view === "subcategories" && (
                <div
                    style={{
                        position: "relative",
                        zIndex: 1,
                        /* paddingTop pushes rows below the blob banner (143px top + 258px height + 20px gap) */
                        paddingTop: 350,
                        
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 52,
                        flex: 1,
                        minHeight: "100vh",
                    }}
                >
                    {/* Back arrow – top right (→ in RTL = go back) */}
                    <button
                        type="button"
                        onClick={goBack}
                        aria-label="חזור"
                        className="focus:outline-none"
                        style={{
                            position: "absolute",
                            right: 20,
                            top: 45,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 8,
                            zIndex: 2,
                        }}
                    >
                        <IoArrowForward size={27} style={{ color: BROWN }} />
                    </button>

                    {/* Category header banner – organic blob SVG background */}
                    <div
                        style={{
                            position: "absolute",
                            left: 0,
                            top: 50,
                            width: 440,
                            height: 258,
                            zIndex: 1,
                            pointerEvents: "none",
                        }}
                    >
                        {/* Blob background SVG */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="440" height="258" viewBox="0 0 440 258" fill="none" style={{ position: "absolute", inset: 0 }}>
                            <g filter="url(#filter0_g_143_663)">
                                <rect x="14.4443" y="31.1477" width="410.111" height="195.538" fill="#FFF8F8" fillOpacity="0.8" />
                            </g>
                            <defs>
                                <filter id="filter0_g_143_663" x="-16.7034" y="0" width="472.407" height="257.834" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                    <feTurbulence type="fractalNoise" baseFrequency="0.032105095684528351 0.032105095684528351" numOctaves="3" seed="3198" />
                                    <feDisplacementMap in="shape" scale="62.295406341552734" xChannelSelector="R" yChannelSelector="G" result="displacedImage" width="100%" height="100%" />
                                    <feMerge result="effect1_texture_143_663">
                                        <feMergeNode in="displacedImage" />
                                    </feMerge>
                                </filter>
                            </defs>
                        </svg>

                        {/* Category name – left side, vertically centered over blob */}
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                display: "flex",
                                flexDirection: "row-reverse",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                gap: 0, // no space between
                            }}
                        >
                            {/* Category image */}
                            {selectedCat?.categoryImage ? (
                                <img
                                    src={sbImg(selectedCat.categoryImage, 170)}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                        width: 85,
                                        height: 85,
                                        objectFit: "cover",
                                        pointerEvents: "none",
                                        borderRadius: 4,
                                        marginLeft: 0,
                                        marginRight: 0,
                                        display: "block",
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: 85,
                                        height: 85,
                                        backgroundColor: "rgba(85,38,12,0.06)",
                                        borderRadius: 4,
                                        marginLeft: 0,
                                        marginRight: 0,
                                    }}
                                />
                            )}
                            <p
                                dir="rtl"
                                style={{
                                    fontFamily: FONT_HEB,
                                    fontWeight: 500,
                                    fontSize: 28,
                                    color: BROWN,
                                    margin: 0,
                                    whiteSpace: "nowrap",
                                    pointerEvents: "auto",
                                    display: "block",
                                }}
                            >
                                {catName(selectedCat)}
                            </p>
                        </div>
                    </div>

                    {/* Subcategory rows – 241×44px with organic blob SVG background */}
                    {subCatsForSelected.map((sub) => (
                        <button
                            key={sub._id}
                            type="button"
                            onClick={() => handleSubClick(sub)}
                            className="focus:outline-none active:scale-[0.97] transition-transform"
                            style={{
                                width: 241,
                                height: 44,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                flexShrink: 0,
                                overflow: "visible",
                            }}
                        >
                            {/* Organic blob background – SVG is 251×54, bleeds 5px on each side */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="251"
                                height="54"
                                viewBox="0 0 251 54"
                                fill="none"
                                aria-hidden="true"
                                style={{
                                    position: "absolute",
                                    left: -5,
                                    top: -5,
                                    pointerEvents: "none",
                                    zIndex: 0,
                                }}
                            >
                                <g filter="url(#filter0_g_124_470)">
                                    <rect x="5" y="5" width="241" height="44" rx="10" fill="#78967A" fillOpacity="0.25" />
                                </g>
                                <defs>
                                    <filter id="filter0_g_124_470" x="0" y="0" width="251" height="54" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                        <feTurbulence type="fractalNoise" baseFrequency="0.20000000298023224 0.20000000298023224" numOctaves="3" seed="5379" />
                                        <feDisplacementMap in="shape" scale="10" xChannelSelector="R" yChannelSelector="G" result="displacedImage" width="100%" height="100%" />
                                        <feMerge result="effect1_texture_124_470">
                                            <feMergeNode in="displacedImage" />
                                        </feMerge>
                                    </filter>
                                </defs>
                            </svg>

                            <IoArrowForward
                                size={14}
                                style={{ position: "absolute", left: 14, color: BROWN, opacity: 0.7, zIndex: 1, transform: "scaleX(-1)" }}
                            />
                            <span
                                style={{
                                    fontFamily: FONT_HEB,
                                    fontSize: 19.924,
                                    fontWeight: 400,
                                    color: BROWN,
                                    textAlign: "center",
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                {subCatName(sub)}
                            </span>
                        </button>
                    ))}
                    {orderingEnabled && (
                        <button type="button" onClick={() => setOrderModalOpen(true)}
                            style={{ background: "#5F6C37", color: "white", border: "none", borderRadius: 50, padding: "14px 40px", fontFamily: FONT_HEB, fontSize: 16, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 18px rgba(0,0,0,0.18)", whiteSpace: "nowrap", direction: "rtl", margin: "8px auto 4px" }}>
                           להזמנה
                        </button>
                    )}
                    <Footer restaurant={restaurant} />
                </div>
            )}

            {/* ══════════════════ DISHES VIEW ═══════════════════════════════════════ */}
            {view === "dishes" && (
                <div
                    style={{
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 196,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 20,
                        flex: 1,
                        minHeight: "100vh",
                    }}
                >
                    {/* ── Header: arrow above, right-aligned, then green blob + name ────── */}
                    <div
                        style={{
                            position: "absolute",
                            top: 50,
                            left: 0,
                            right: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            zIndex: 2,
                            gap: 10,
                        }}
                    >
                        {/* Back arrow – above blob/name row, right side */}
                        <button
                            type="button"
                            onClick={goBack}
                            aria-label="חזור"
                            className="focus:outline-none"
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 4,
                                zIndex: 2,
                                alignSelf: "flex-end",
                                marginBottom: 4,
                                marginRight: "calc(50% - 181px)",
                            }}
                        >
                            <IoArrowForward
                                size={27}
                                style={{ color: BROWN, display: "block" }}
                            />
                        </button>

                        {/* Header inner: 362px container matching Figma */}
                        <div style={{ position: "relative", width: 362, height: 59, alignSelf: "center" }}>
                            {/* Green organic blob background – user-provided SVG */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="337"
                                height="73"
                                viewBox="0 0 337 73"
                                fill="none"
                                aria-hidden="true"
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    pointerEvents: "none",
                                    zIndex: 0,
                                }}
                            >
                                <g filter="url(#dishes-header-filter)">
                                    <rect x="6.70459" y="6.70454" width="323.159" height="59" rx="13.4091" fill="#78967A" fillOpacity="0.25" />
                                </g>
                                <defs>
                                    <filter id="dishes-header-filter" x="0" y="0" width="336.568" height="72.4091" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                        <feTurbulence type="fractalNoise" baseFrequency="0.14915254712104797 0.14915254712104797" numOctaves="3" seed="5379" />
                                        <feDisplacementMap in="shape" scale="13.409090042114258" xChannelSelector="R" yChannelSelector="G" result="displacedImage" width="100%" height="100%" />
                                        <feMerge result="effect1_texture_143_771">
                                            <feMergeNode in="displacedImage" />
                                        </feMerge>
                                    </filter>
                                </defs>
                            </svg>

                            {/* Category / subcategory name – centered inside blob */}
                            <p
                                dir="rtl"
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    textAlign: "center",
                                    fontFamily: FONT_HEB,
                                    fontWeight: 400,
                                    fontSize: 26.717,
                                    color: BROWN,
                                    margin: 0,
                                    whiteSpace: "nowrap",
                                    zIndex: 1,
                                }}
                            >
                                {selectedSub ? subCatName(selectedSub) : catName(selectedCat)}
                            </p>
                        </div>
                    </div>

                    {/* ── Empty state ──────────────────────────────────────────────────────── */}
                    {selectedDishes.length === 0 && (
                        <p style={{ fontFamily: FONT_HEB, color: BROWN, opacity: 0.5, padding: "40px 0" }}>
                            אין מנות להצגה
                        </p>
                    )}

                    {/* ── Dish cards ───────────────────────────────────────────────────────── */}
                    {selectedDishes.map((dish) => (
                        <div
                            key={dish._id}
                            style={{
                                position: "relative",
                                width: 334,
                                minHeight: 158,
                                backgroundColor: "rgba(255,248,248,0.8)",
                                borderRadius: 15,
                                overflow: "visible",
                                flexShrink: 0,
                            }}
                        >
                            {/* Dish image – overflows left edge, positioned top-left */}
                            {dish.dishImage ? (
                                <img
                                    src={sbImg(dish.dishImage, 300)}
                                    alt={dishTitle(dish)}
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                        position: "absolute",
                                        left: 5,
                                        top: 9,
                                        width: 150,
                                        height: 150,
                                        objectFit: "contain",
                                        pointerEvents: "none",
                                    }}
                                />
                            ) : (
                                <div style={{
                                    position: "absolute",
                                    left: 5,
                                    top: 9,
                                    width: 150,
                                    height: 150,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    pointerEvents: "none",
                                    opacity: 0.25,
                                    gap: 6,
                                }}>
                                    <img src={LOGO_IMG} alt="" style={{ width: 80, height: 80, objectFit: "contain" }} />
                                    <span style={{ fontFamily: FONT_HEB, fontSize: 11, color: BROWN, textAlign: "center" }}>אין תמונה</span>
                                </div>
                            )}

                            {/* Text area – right side of card */}
                            <div
                                dir="rtl"
                                style={{
                                    paddingTop: 20,
                                    paddingBottom: 40,
                                    paddingLeft: 14,
                                    paddingRight: 14,
                                    marginLeft: 132,
                                }}
                            >
                                {/* Dish name */}
                                <p
                                    style={{
                                        fontFamily: FONT_HEB,
                                        fontWeight: 400,
                                        fontSize: 20,
                                        color: BROWN,
                                        margin: 0,
                                        textAlign: "right",
                                    }}
                                >
                                    {dishTitle(dish)}
                                </p>

                                {/* Description */}
                                {dishDesc(dish) && (
                                    <p
                                        style={{
                                            fontFamily: FONT_HEB,
                                            fontSize: 15,
                                            color: `rgba(85,38,12,0.5)`,
                                            margin: "6px 0 0",
                                            textAlign: "right",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {dishDesc(dish)}
                                    </p>
                                )}
                            </div>

                            {/* Price — single price OR size pills */}
                            {dish.sizes?.length > 0 ? (
                                <div
                                    dir="rtl"
                                    style={{
                                        position: "absolute",
                                        right: 14,
                                        bottom: 10,
                                        left: 0,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 6,
                                        justifyContent: "flex-start",
                                    }}
                                >
                                    {dish.sizes.map((s, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 3,
                                                backgroundColor: "rgba(120,150,122,0.15)",
                                                borderRadius: 8,
                                                padding: "2px 8px",
                                            }}
                                        >
                                            <span style={{ fontFamily: FONT_HEB, fontSize: 12, color: BROWN }}>{s.name}</span>
                                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: BROWN, fontWeight: 500 }}>
                                                ₪{s.price}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : dish.price != null && dish.price !== "" ? (
                                <div
                                    style={{
                                        position: "absolute",
                                        right: 14,
                                        bottom: 14,
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 2,
                                    }}
                                >
                                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 16, color: BROWN }}>
                                        {dish.price}
                                    </span>
                                    <span style={{ fontFamily: FONT_HEB, fontSize: 13, color: BROWN, opacity: 0.8 }}>₪</span>
                                </div>
                            ) : null}

                        </div>
                    ))}


                    {/* ── "להזמנה" button between dishes and footer ── */}
                    {orderingEnabled && (
                        <button
                            type="button"
                            onClick={() => setOrderModalOpen(true)}
                            style={{
                                background: "#5F6C37",
                                color: "white",
                                border: "none",
                                borderRadius: 50,
                                padding: "14px 40px",
                                fontFamily: FONT_HEB,
                                fontSize: 16,
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
                                whiteSpace: "nowrap",
                                direction: "rtl",
                                margin: "8px 0 4px",
                            }}
                        >
                            להזמנה
                        </button>
                    )}

                    <Footer restaurant={restaurant} />
                </div>
            )}
        </div>
        </>
    );
}

export default Home;
